import React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  AcknowledgmentOverrideListRequest,
  useFetchAcknowledgmentOverridesQuery,
} from '../acknowledgmentoverride-api';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@ant-design/pro-layout';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Dropdown,
  Input,
  Menu,
  message,
  Modal,
  Space,
  Table,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  HomeOutlined,
  MoreOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
  TablePaginationConfig,
} from 'antd/lib/table/interface';
import { AcknowledgmentOverrideViewModel, PaginatedResult} from '../../api/models';
import { DefaultPage, DefaultPageSize } from '../../../core/defaults';
import Title from 'antd/lib/typography/Title';
import { getLink } from '../../../App-router';
import { TableState } from '../../../components/table/model';
import getSortBy from '../../../components/table/sorter';
import AuditLogsScreen from '../../../components/common/auditLogs';
import { useMasterDataDelete } from '../../masterdata/useMasterDataDelete';
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export default function AcknowledgmentOverridesScreen(props: any) {
  const location = useLocation();
  const query = useQuery();
  const params: any = useParams<any>();
  const navigate = useNavigate();
  const [showAuditLogs, setShowAuditLogs] = useState<boolean>(false);
  const [entityId, setEntityId] = useState<number>(0);

  // Helper function to get sortOrder for a column based on current URL state
  const getSortOrderForColumn = (columnKey: string): 'ascend' | 'descend' | null => {
    const sortBy = query.get('sortBy') || '';
    if (!sortBy) return null;
    
    const columnMapping: { [key: string]: string } = {
      'Id': 'Id',
      'transactiontype': 'TransactionType',
      'interchangesenderqualifier': 'InterchangeSenderQualifier',
      'interchangesenderid': 'InterchangeSenderID',
      'interchangereceiverqualifier': 'InterchangeReceiverQualifier',
      'interchangereceiverid': 'InterchangeReceiverID',
    };
    
    const backendColumnName = columnMapping[columnKey] || columnKey;
    const sortByParts = sortBy.trim().split(/\s+/);
    const sortColumnName = sortByParts[0];
    const sortDirection = sortByParts[1]?.toLowerCase() || '';
    
    if (sortColumnName.toLowerCase() === backendColumnName.toLowerCase()) {
      return sortDirection === 'desc' ? 'descend' : 'ascend';
    }
    return null;
  };

  const acknowledgmentoverrideTable: TableState = {
    columns: [
      {
        title: 'Id',
        dataIndex: '',
        key: 'Id',
        render: (acknowledgmentoverride: AcknowledgmentOverrideViewModel) => (
          <>
              <Space>
                <span>{acknowledgmentoverride.id}</span>
              </Space>
          </>
        ),
        sorter: true,
        sortOrder: getSortOrderForColumn('Id'),
        fixed: 'left',
      },
      {
        title: 'TransactionType',
        dataIndex: '',
        key: 'transactiontype',
        render: (acknowledgmentoverride: AcknowledgmentOverrideViewModel) => (
          <>
              <span>{acknowledgmentoverride.transactionType}</span>
          </>
        ),
        sorter: true,
        sortOrder: getSortOrderForColumn('transactiontype'),
      },
      {
        title: 'InterchangeSenderQualifier',
        dataIndex: '',
        key: 'interchangesenderqualifier',
        render: (acknowledgmentoverride: AcknowledgmentOverrideViewModel) => (
          <>
              <span>{acknowledgmentoverride.interchangeSenderQualifier}</span>
          </>
        ),
        sorter: true,
        sortOrder: getSortOrderForColumn('interchangesenderqualifier'),
      },
      {
        title: 'InterchangeSenderID',
        dataIndex: '',
        key: 'interchangesenderid',
        render: (acknowledgmentoverride: AcknowledgmentOverrideViewModel) => (
          <>
              <span>{acknowledgmentoverride.interchangeSenderID}</span>
          </>
        ),
        sorter: true,
        sortOrder: getSortOrderForColumn('interchangesenderid'),
      },
      {
        title: 'InterchangeReceiverQualifier',
        key: 'interchangereceiverqualifier',
        dataIndex: '',
        render: (acknowledgmentoverride: AcknowledgmentOverrideViewModel) => (
          <>
              <span>{acknowledgmentoverride.interchangeReceiverQualifier}</span>
          </>
        ),
        sorter: true,
        sortOrder: getSortOrderForColumn('interchangereceiverqualifier'),
      },

      {
        title: 'InterchangeReceiverID',
        dataIndex: '',
        key: 'interchangereceiverid',
        render: (acknowledgmentoverride: AcknowledgmentOverrideViewModel) => (
          <>
              <span>{acknowledgmentoverride.interchangeReceiverID}</span>
          </>
        ),
        sorter: true,
        sortOrder: getSortOrderForColumn('interchangereceiverid'),
      },
      {
        title: 'GroupSenderID',
        dataIndex: '',
        key: 'groupsenderid',
        render: (acknowledgmentoverride: AcknowledgmentOverrideViewModel) => (
          <>
              <span>{acknowledgmentoverride.groupSenderID}</span>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: (query.get('sortBy') || '').toLowerCase().includes('groupsenderid')
          ? ((query.get('sortBy') || '').toLowerCase().includes('desc') ? 'descend' : 'ascend')
          : null,
      },
      {
        title: 'GroupReceiverID',
        dataIndex: '',
        key: 'groupreceiverid',
        render: (acknowledgmentoverride: AcknowledgmentOverrideViewModel) => (
          <>
              <span>{acknowledgmentoverride.groupReceiverID}</span>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: (query.get('sortBy') || '').toLowerCase().includes('groupreceiverid')
          ? ((query.get('sortBy') || '').toLowerCase().includes('desc') ? 'descend' : 'ascend')
          : null,
      },
      {
        title: 'AcknowledgmentOverride',
        key: 'acknowledgmentoverride',
        dataIndex: '',
        render: (acknowledgmentoverride: AcknowledgmentOverrideViewModel) => (
          <>
              <span>{acknowledgmentoverride.acknowledgmentOverride?'True':'False'}</span>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: (query.get('sortBy') || '').toLowerCase().includes('acknowledgmentoverride')
          ? ((query.get('sortBy') || '').toLowerCase().includes('desc') ? 'descend' : 'ascend')
          : null,
      },
      {
        title: 'IsActive',
        key: 'isactive',
        dataIndex: '',
        render: (acknowledgmentoverride: AcknowledgmentOverrideViewModel) => (
          <>
              <span>{acknowledgmentoverride.isActive?'True':'False'}</span>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: (query.get('sortBy') || '').toLowerCase().includes('isactive')
          ? ((query.get('sortBy') || '').toLowerCase().includes('desc') ? 'descend' : 'ascend')
          : null,
      },      
      {
        title: 'Actions',
        key: 'action',
        dataIndex: '',
        fixed:'right',
        render: (acknowledgmentoverride: AcknowledgmentOverrideViewModel) => (
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            overlay={
              <Menu style={{ padding: '8px 8px' }}>
                <Menu.Item icon={<EditOutlined />} key="1">
                  <Link
                    to={getLink('AcknowledgmentOverrideEdit', {
                      tenantId: params.tenantId,
                      Id: acknowledgmentoverride.id
                    })}
                  >
                    Edit
                  </Link>
                </Menu.Item>
                <Menu.Item icon={<InfoCircleOutlined />} key="3">
                <Link to="#" onClick={()=>setShowAuditLogs(true)}>
                    Audit Logs
                  </Link>
                </Menu.Item>
                <Menu.Item
                  icon={<DeleteOutlined />}
                  key="2"
                  disabled={!!(deletingId && deletingId === acknowledgmentoverride.id)}
                  onClick={() =>
                    deleteEntity(acknowledgmentoverride.id ?? 0, String(acknowledgmentoverride.id ?? ''))
                  }
                >
                  {deletingId === acknowledgmentoverride.id ? 'Deleting...' : 'Delete'}
                </Menu.Item>
              </Menu>
            }
          >
            <MoreOutlined />
          </Dropdown>
        ),
      },
    ],
  };

  const getCurrentState = (): AcknowledgmentOverrideListRequest => {
    const urlParams = new URLSearchParams(location.search);
    return {
      current: Number(urlParams.get('p')) || DefaultPage,
      pageSize: Number(urlParams.get('size')) || DefaultPageSize,
      filter: urlParams.get('q'),
      sortBy: urlParams.get('sortBy') || 'EnteredByDate Desc',
    };
  };

  const [pagination, setPagination] = useState<AcknowledgmentOverrideListRequest>(getCurrentState());

  const {
    data: pagedData = {},
    isFetching,
    error,
  }: {
    data?: PaginatedResult<AcknowledgmentOverrideViewModel> | undefined;
    isFetching?: boolean | undefined;
    error?: any;
  } = useFetchAcknowledgmentOverridesQuery(pagination);

  useEffect(() => {
    const newState = getCurrentState();
    setPagination(newState);
  }, [location.search]);

  const {
    deletingId,
    isDeleting: isDeleteLoading,
    deleteError,
    deleteErrorMessage,
    deleteEntity,
  } = useMasterDataDelete({
    entityType: 'AcknowledgmentOverrides',
    tagType: 'AcknowledgmentOverride',
    entityLabel: 'acknowledgment override',
  });
  const { confirm } = Modal;

  const onCreateNew = useCallback(
    () => navigate(getLink('AcknowledgmentOverrideEdit', { ...params, Id: 'new' })),
    [params, navigate]
  );

  const handleTableChange = (
    paginationConfig: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<AcknowledgmentOverrideViewModel> | SorterResult<AcknowledgmentOverrideViewModel>[],
    extra: TableCurrentDataSource<AcknowledgmentOverrideViewModel>
  ) => {
    // Handle sorter - it can be null, undefined, a single object, or an array
    const sorterResult = Array.isArray(sorter) ? sorter[0] : sorter;
    const { columnKey, order } = (sorterResult || {}) as SorterResult<AcknowledgmentOverrideViewModel>;

    const defaultSort = 'EnteredByDate Desc';
    // Read current sort from URL to ensure fresh value
    const urlParams = new URLSearchParams(location.search);
    const currentSortBy = urlParams.get('sortBy') || defaultSort;
    const currentPage = Number(urlParams.get('p')) || 1;
    
    // Determine new page number - ensure it's always a valid number
    let newPage = paginationConfig.current;
    if (!newPage || newPage < 1) {
      newPage = currentPage;
    }
    
    // Determine new sort - always ensure it's a valid string
    let newSortBy: string = defaultSort;

    if (columnKey && order) {
      const columnMapping: { [key: string]: string } = {
        'Id': 'Id',
        'transactiontype': 'TransactionType',
        'interchangesenderqualifier': 'InterchangeSenderQualifier',
        'interchangesenderid': 'InterchangeSenderID',
        'interchangereceiverqualifier': 'InterchangeReceiverQualifier',
        'interchangereceiverid': 'InterchangeReceiverID',
      };
      
      const clickedColumn = columnKey.toString();
      const backendColumnName = columnMapping[clickedColumn] || clickedColumn;
      newSortBy = getSortBy(backendColumnName, order);
      
      if (newSortBy !== currentSortBy) {
        newPage = 1;
      }
    } else if (columnKey && !order && currentSortBy !== defaultSort) {
      // User is cancelling an active sort
      newSortBy = defaultSort;
      newPage = 1;
    } else {
      // Pagination only - preserve current sort
      newSortBy = currentSortBy || defaultSort;
    }
    
    // Final safety check - ensure newSortBy is never empty
    if (!newSortBy || newSortBy.trim() === '') {
      newSortBy = defaultSort;
    }

    const newUrl = getLink(
      'AcknowledgmentOverrideList',
      { ...params },
      {
        sortBy: newSortBy,
        p: newPage,
        size: paginationConfig.pageSize,
        q: urlParams.get('q') || '',
      }
    );
    
    navigate(newUrl, { replace: false });
  };

  const handleSearch = (searchText: string) => {
    navigate(
      getLink(
        'AcknowledgmentOverrideList',
        { ...params },
        {
          p: DefaultPage,
          size: pagination.pageSize,
          q: encodeURIComponent(searchText?.length ? `InterchangeSenderID:*${searchText.replaceAll(" ", "\\ ")}* OR InterchangeReceiverID:*${searchText.replaceAll(" ", "\\ ")}*
          OR GroupSenderID:*${searchText.replaceAll(" ", "\\ ")}* OR GroupReceiverID:*${searchText.replaceAll(" ", "\\ ")}*` : ''),
          sortBy: pagination.sortBy || '',  // Preserve current sort
        }
      )
    );
  };
  return (
    <>
      <PageHeader
        extra={
            <Space>
                  <Button htmlType="button" data-testid={'create-new-acknowledgmentoverride'} onClick={onCreateNew}>
                  New Acknowledgment Override
                  </Button>
                </Space>
        }
        title={
          <Title
            level={4}
            style={{
              marginBottom: 0,
              display: 'inline-block',
            }}
          >
            Acknowledgment Override
          </Title>
        }
        breadcrumb={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to={getLink('home', { tenantId: params.tenantId })}>
              <HomeOutlined />
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>              
              <Link to={getLink('cob', { tenantId: params.tenantId })}>
              Dashboard
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item key="acknowledgmentoverrides">
              <Link to={getLink('AcknowledgmentOverrideList', { tenantId: params.tenantId })}>Acknowledgment Override</Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        }
      >
        <Card>
          {error?.status && (
            <Alert message="Error" description={error.data} type="error" showIcon />
          )}
          {deleteError && (
            <Alert message="Error" description={deleteErrorMessage} type="error" showIcon />
          )}
          <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Input.Search
              placeholder="Search by InterchangeSenderID, InterchangeReceiverID, GroupSenderID, GroupReceiverID"
              onSearch={handleSearch}
              enterButton
              style={{ width: 780 }}
              loading={isFetching}
              allowClear
            />
            <Table scroll={{ x: true }}
              dataSource={pagedData.results}
              onChange={handleTableChange}
              rowKey={'id'}
              onRow={(record, rowIndex) => {
                return {
                  onClick: event => { 
                    setEntityId(record?.id||0);
                  },
                };
              }}
              pagination={{
                ...pagination,
                total: pagedData.totalCount,
                showTotal: (total) => `Total ${total} items`,
                pageSizeOptions: ["10", "20", "50","100"],
                showSizeChanger: true,
                locale: { items_per_page: " / Page" }
              }}
              columns={acknowledgmentoverrideTable.columns}
              loading={isFetching}
            />
          </Space>
        </Card>
        {showAuditLogs?<AuditLogsScreen entityId={entityId} entityName={'AcknowledgmentOverride'} showAuditLogs={setShowAuditLogs}/>:<></>}
      </PageHeader>
    </>
  );
}
